import React, {useState} from 'react';

import {Button, CheckBox, Input} from '@/components';
import LimitStatusNote from '@/screens/Merchants/common/LimitStatusNote';
import {merchantService} from '@/services/merchants';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

// The server's sentinel for "no limit". Every other value is a USD credit line,
// where 0 means the Fee wallet may not go negative at all.
const NO_LIMIT = -1;

type FeeWalletOverdraftLimitFormProps = {
  businessId: string;

  currentLimitUSD?: number;
};

/**
 * Sets how far a business's Fee wallet may go negative before payouts are
 * suspended. Only rendered for businesses approved for fee-wallet settlement
 * (see `meta.flags.isApprovedForFeeWallet`)
 */
const FeeWalletOverdraftLimitForm = ({
  businessId,
  currentLimitUSD,
}: FeeWalletOverdraftLimitFormProps) => {
  const [, setSavedLimitUSD] = useState(currentLimitUSD);
  const [noLimit, setNoLimit] = useState(currentLimitUSD === NO_LIMIT);
  const [limitUSD, setLimitUSD] = useState(
    currentLimitUSD === undefined || currentLimitUSD === NO_LIMIT
      ? ''
      : String(currentLimitUSD),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const parsedLimit = Number(limitUSD);
  const isLimitValid =
    limitUSD !== '' && Number.isFinite(parsedLimit) && parsedLimit >= 0;
  const canSubmit = !isSubmitting && (noLimit || isLimitValid);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const updated =
        await merchantService.updateBusinessFeeWalletOverdraftLimit(
          businessId,
          noLimit ? NO_LIMIT : parsedLimit,
        );
      setSavedLimitUSD(updated?.meta?.feeWalletOverdraftLimitUSD);
      showSuccessMessage('Fee wallet overdraft limit updated');
    } catch (e) {
      showErrorMessage(
        `Failed to update the fee wallet overdraft limit: ${getErrorMessage(
          e,
          String(e),
        )}`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="py-3 px-3 bg-gray-100 dark:bg-gray-900 rounded">
      <p className="text-slate-500 text-sm dark:text-slate-100 mb-2">
        Fee Wallet Overdraft Limit in USD
      </p>

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="w-full md:w-48">
          <Input
            type="number"
            min={0}
            aria-label="Overdraft limit (USD)"
            placeholder="Limit in USD"
            value={limitUSD}
            disabled={noLimit}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setLimitUSD(e.target.value)
            }
            className="w-full isFull"
          />
        </div>
        <CheckBox
          label="No limit"
          labelClassName="mb-0"
          checked={noLimit}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setNoLimit(e.target.checked)
          }
        />
        <Button
          variant="solid"
          disabled={!canSubmit}
          onClick={() => {
            // handleSubmit toasts its own failures rather than rethrowing.
            void handleSubmit();
          }}
        >
          Save
        </Button>
      </div>
      <div className="mt-3">
        <LimitStatusNote inUse>
          How far this business&apos;s Fee wallet can go negative, in USD,
          before its payouts are suspended. 0 means it cannot go negative; tick
          &ldquo;No limit&rdquo; to never suspend a payout for this.
        </LimitStatusNote>
      </div>
    </div>
  );
};

export default FeeWalletOverdraftLimitForm;
